/**
 * CartProvider / ``useCart`` — app-scoped local cart state (ТЗ §8-10).
 *
 * Holds the in-memory selection and exposes typed mutators. Mounted once near the app root
 * so the selection survives navigation within a session (it is intentionally not persisted —
 * a cart is a transient, single-session intent).
 */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useReducer,
  type ReactNode,
} from "react";

import type { ProductListItem } from "@entities/product";

import {
  EMPTY_CART,
  cartReducer,
  lineQty,
  type CartItems,
} from "./model";

export interface CartContextValue {
  readonly items: CartItems;
  readonly add: (product: ProductListItem) => void;
  readonly increment: (productId: number) => void;
  readonly decrement: (productId: number) => void;
  readonly remove: (productId: number) => void;
  readonly clear: () => void;
  /** Quantity selected for a product (0 when absent). */
  readonly qtyOf: (productId: number) => number;
}

const CartContext = createContext<CartContextValue | null>(null);

export interface CartProviderProps {
  readonly children: ReactNode;
}

export function CartProvider({ children }: CartProviderProps): JSX.Element {
  const [items, dispatch] = useReducer(cartReducer, EMPTY_CART);

  const add = useCallback((product: ProductListItem) => {
    dispatch({ type: "add", product });
  }, []);
  const increment = useCallback((productId: number) => {
    dispatch({ type: "increment", productId });
  }, []);
  const decrement = useCallback((productId: number) => {
    dispatch({ type: "decrement", productId });
  }, []);
  const remove = useCallback((productId: number) => {
    dispatch({ type: "remove", productId });
  }, []);
  const clear = useCallback(() => {
    dispatch({ type: "clear" });
  }, []);
  const qtyOf = useCallback(
    (productId: number) => lineQty(items, productId),
    [items],
  );

  const value = useMemo<CartContextValue>(
    () => ({ items, add, increment, decrement, remove, clear, qtyOf }),
    [items, add, increment, decrement, remove, clear, qtyOf],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

/** Access the cart. Throws if used outside {@link CartProvider}. */
export function useCart(): CartContextValue {
  const value = useContext(CartContext);
  if (value === null) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return value;
}
